import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useDefinedQuery, type QueryDef } from '@/hooks/useDefinedQuery'
import { getFeatures } from '@/services/api/features'
import { useAuthStore } from '@/store/authStore'

const STALE_10M = 1000 * 60 * 10

const featuresQueries = {
  all: {
    queryKey: () => ['features'] as const,
    queryFn: getFeatures,
    staleTime: STALE_10M,
    auth: 'public',
  } satisfies QueryDef<Awaited<ReturnType<typeof getFeatures>>>,
}

function useFeatureFlags() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const queryClient = useQueryClient()

  const query = useDefinedQuery(featuresQueries.all)

  // Feature gates can depend on the logged-in actor (e.g. per-user/per-group
  // targeting), so re-fetch whenever auth state changes rather than serving a
  // stale pre-login (or previous-user) snapshot for the full staleTime.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: featuresQueries.all.queryKey() })
  }, [isAuthenticated, queryClient])

  return query
}

export function useFeatureFlag(flagName: string): boolean {
  const { data } = useFeatureFlags()
  return data?.features[flagName] ?? false
}
